'use client';
import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';

import type { FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { fetchProductSegmentsProductsBySegmentType } from '@eliadmin/core/src/api/product-segments/product-segments-api';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useQuery } from '@tanstack/react-query';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../shared/heading';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Button } from '../ui/button';

import { MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { Reveal } from '../ui/reveal';

interface HomepageCarouselProps {
  className?: {
    section?: string;
    container?: string;
  };
  title: string;
  subtitle?: string;
  description?: string;
  products: ProductDisplayData[];
  locale?: string;
  query?: FetchProductQuery;
  personalizationKey?: string;
  segmentType?: string;
}

export function HomepageCarousel({
  className,
  title,
  subtitle,
  description,
  products: initialProducts,
  locale,
  query,
  personalizationKey,
  segmentType,
}: HomepageCarouselProps) {
  const { t } = useTranslations();
  const carouselContext = useCarouselContext();
  const me = useCustomerStore(state => state.me);
  const isInitialized = useCustomerStore(state => state.isInitialized);
  const authToken = me?.authToken;
  const personalizedProductsQuery = useQuery<ProductDisplayData[]>({
    queryKey: [
      CacheKey.PRODUCTS,
      'homepage-carousel',
      personalizationKey,
      segmentType,
      locale,
      query,
      me?.customer.id ?? 'guest',
    ],
    queryFn: () =>
      (segmentType
        ? fetchProductSegmentsProductsBySegmentType({
            locale: locale!,
            segmentType,
            query,
            authToken,
          })
        : fetchProducts({ locale: locale!, query, authToken })
      ).then(response => response.data),
    enabled: isInitialized && !!authToken && !!locale && !!query,
    placeholderData: initialProducts,
    staleTime: 0,
  });
  const products = personalizedProductsQuery.data ?? initialProducts;

  if (!carouselContext) {
    throw new Error('HomepageCarousel must be used within a CarouselProvider');
  }

  if (segmentType && !products.length) return null;

  return (
    <section className={cn('custom-section', '!pr-0', className?.section)}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:gap-8 md:gap-10 lg:gap-12',
          className?.container,
        )}
      >
        <div className="flex w-full items-end justify-between gap-6">
          <div className="flex w-full max-w-[990px] flex-col items-start justify-start gap-6">
            <Reveal>
              <Heading subtitle={subtitle} title={title} />
            </Reveal>
            {description && (
              <Reveal>
                <p className="w-full max-w-[505px] text-balance text-sm font-light text-black/70 sm:text-base">
                  {description}
                </p>
              </Reveal>
            )}
          </div>
          <div className="hidden items-center justify-end gap-4 pr-6 lg:flex xl:pr-0">
            <Button
              size={'icon'}
              variant={'alternative-default'}
              disabled={!carouselContext.api}
              onClick={() => carouselContext.api?.scrollPrev()}
              aria-label={t('text3525')}
            >
              <MdOutlineKeyboardArrowLeft size={20} />
            </Button>
            <Button
              size={'icon'}
              variant={'alternative-default'}
              disabled={!carouselContext.api}
              onClick={() => carouselContext.api?.scrollNext()}
              aria-label={t('text3526')}
            >
              <MdOutlineKeyboardArrowRight size={20} />
            </Button>
          </div>
        </div>
        <Carousel
          mobileProgressBar
          className={{
            container: 'w-full gap-4 sm:gap-3',
            wrapper: 'w-full',
            main: 'w-full pb-10 sm:pb-0',
            slide: 'w-full max-w-[262px] sm:max-w-[280px] lg:max-w-[301px]',
          }}
          slides={products.map(product => (
            <ProductCard
              key={product?.slug}
              product={product}
              deferImage
              imageSizes="(min-width: 1024px) 287px, (min-width: 640px) 266px, 248px"
            />
          ))}
        />
      </div>
    </section>
  );
}
